import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Chip from '@material-ui/core/Chip';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ScheduleIcon from '@material-ui/icons/Schedule';
import SendIcon from '@material-ui/icons/Send';
import WarningIcon from '@material-ui/icons/Warning';
import { makeStyles } from '@material-ui/core/styles';

import {
  BODY_MAX_LENGTH,
  bodyLengthWithout,
  contactsLabel,
  moneyLabel,
  paramsFor,
  renderTemplate,
  statusInfo,
} from '../../../constants/whatsapp';
import { canReask, listStats, MAX_ASKS, REASK_DAYS } from '../../../constants/whatsapp-contacts';
import { actions as mainActions } from '../../Root/actions';
import { actions as whatsappActions } from '../actions';
import { buildOccurrences, defaultRule, formatFull, payloadOf } from '../schedule';
import AudienceDrawer from '../components/AudienceDrawer';
import ChannelSwitch from '../components/ChannelSwitch';
import FieldLabel from '../components/FieldLabel';
import ScheduleEditor from '../components/ScheduleEditor';
import SendBroadcastDialog from '../components/SendBroadcastDialog';
import TemplateThumb from '../components/TemplateThumb';
import TestSendDialog from '../components/TestSendDialog';
import WhatsappPreview from '../components/WhatsappPreview';
import useRenderedImage from '../hooks/useRenderedImage';
import { NAME as WHATSAPP_NAME } from '../reducer';
import { colors } from '../theme';
import { KYIV_ZONE, kyivNow } from '../kyivTime';
import { clearDraft, loadDraft, saveDraft } from '../draft';

function mapStateToProps(state) {
  return {
    ...state[WHATSAPP_NAME],
  };
}

const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ ...mainActions, ...whatsappActions }, dispatch);

const useStyles = makeStyles((theme) => ({
  card: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2.5),
    borderRadius: 10,
  },
  // Заголовок карточки и справа «Очистить», если в форме уже что-то есть.
  cardHead: {
    display: 'flex',
    alignItems: 'baseline',
    marginBottom: theme.spacing(2.5),
    '& $cardTitle': { marginBottom: 0, flexGrow: 1 },
  },
  draftNote: {
    color: colors.textDim,
    fontSize: '12px',
    marginRight: theme.spacing(1),
  },
  clearButton: {
    color: colors.textDim,
  },
  cardTitle: {
    color: colors.text,
    fontWeight: 700,
    fontSize: '16px',
    marginBottom: theme.spacing(2.5),
  },
  counter: {
    display: 'block',
    textAlign: 'right',
    marginTop: theme.spacing(0.5),
  },
  templateInfo: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2.5),
  },
  chipPending: {
    backgroundColor: colors.warnBg,
    color: colors.warnText,
    marginRight: theme.spacing(1),
  },
  autoValue: {
    padding: theme.spacing(1.5, 1.75),
    borderRadius: 4,
    border: `1px dashed ${colors.textDim}`,
    color: theme.palette.text.secondary,
    fontSize: '14px',
  },
  audience: {
    marginBottom: theme.spacing(2.5),
  },
  audienceChips: {
    display: 'flex',
    flexWrap: 'wrap',
    marginTop: theme.spacing(0.5),
  },
  chipOk: {
    backgroundColor: colors.okBg,
    color: colors.okText,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  chipWarn: {
    backgroundColor: colors.warnBg,
    color: colors.warnText,
    marginRight: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  hint: {
    display: 'block',
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  },
  sticky: {
    position: 'sticky',
    top: theme.spacing(11),
  },
  sendButton: {
    marginTop: theme.spacing(2),
    backgroundColor: colors.accent,
    color: '#ffffff',
    padding: theme.spacing(1.35),
    fontWeight: 600,
    '&:hover': {
      backgroundColor: colors.accentHover,
    },
  },
  testButton: {
    marginTop: theme.spacing(1),
    padding: theme.spacing(1.1),
  },
}));

const clockOf = (date) =>
  date.toLocaleTimeString('uk-UA', {
    timeZone: KYIV_ZONE,
    hour: '2-digit',
    minute: '2-digit',
  });

const currentTime = () => clockOf(new Date());

const emptyErrors = {
  name: '',
  list: '',
  template: '',
  params: '',
  schedule: '',
};

const todayText = () =>
  new Date().toLocaleDateString('uk-UA', {
    timeZone: KYIV_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });

const initialValues = (template) => {
  const values = {};
  template.params.forEach((param) => {
    values[param.key] = param.today ? todayText() : '';
  });
  return values;
};

function CreateBroadcast(props) {
  const classes = useStyles();
  const { contacts, lists, templates } = props;

  const [limits, setLimits] = useState(null);
  // Цена сообщения по данным Meta (по категориям); пока не пришла или не известна, стоимость не считаем.
  const [pricing, setPricing] = useState(null);

  // Сколько людей ещё можно начать писать сегодня (лимит номера у Meta).
  const refreshLimits = () =>
    props.fetchAccount(true).then((result) => {
      if (result.ok) setLimits(result.data.limits);
    });

  useEffect(() => {
    props.fetchBroadcastLists();
    props.fetchTemplates();
    props.fetchCompanies();
    props.fetchConsents();
    refreshLimits();
    props.fetchPricing(30).then((result) => {
      if (result.ok) setPricing(result.data);
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Черновик с прошлого раза (ушли на «Списки» и вернулись): форма начинается с него.
  const [draft] = useState(loadDraft);
  const [draftRestored, setDraftRestored] = useState(Boolean(draft));
  const [name, setName] = useState(draft ? draft.name || '' : '');
  const [listId, setListId] = useState(draft ? draft.listId || '' : '');
  const [templateId, setTemplateId] = useState(draft ? draft.templateId || '' : '');
  const [values, setValues] = useState(draft ? draft.values || {} : {});
  // Поле, в которое текст не поместился целиком (вставили или печатают сверх лимита этого поля):
  // предупреждение показываем только у него.
  const [overflowKey, setOverflowKey] = useState('');
  const [mode, setMode] = useState(draft ? draft.mode || 'now' : 'now');
  // Запрос согласия: повторить ещё и тем, кто получил его раньше и молчит.
  const [includeSilent, setIncludeSilent] = useState(draft ? Boolean(draft.includeSilent) : false);
  const [rule, setRule] = useState(() => (draft && draft.rule ? draft.rule : defaultRule()));
  const [errors, setErrors] = useState(emptyErrors);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  // Всё введённое сразу ложится в черновик; пустая форма черновика не оставляет.
  useEffect(() => {
    if (name || listId || templateId) {
      saveDraft({ name, listId, templateId, values, mode, includeSilent, rule });
    } else {
      clearDraft();
    }
  }, [name, listId, templateId, values, mode, includeSilent, rule]);

  const selectedList = useMemo(
    () => lists.find((item) => item.id === listId),
    [lists, listId]
  );

  const stats = useMemo(
    () =>
      selectedList
        ? listStats(selectedList, contacts)
        : { total: 0, optIn: 0 },
    [selectedList, contacts]
  );

  const scheduled = mode === 'schedule';
  const schedule = useMemo(() => buildOccurrences(rule, kyivNow()), [rule]);

  const sendableTemplates = useMemo(
    () => templates.filter((item) => item.supported),
    [templates]
  );

  const templateLabel = (item) =>
    `${item.title}${item.consentButtons ? ' (запрос согласия)' : ''}${
      item.status !== 'approved' ? ` — ${statusInfo(item.status).label}` : ''
    }`;

  const template = useMemo(
    () => templates.find((item) => item.id === templateId),
    [templates, templateId]
  );

  const handleTemplateChange = (event) => {
    const nextId = event.target.value;
    const next = templates.find((item) => item.id === nextId);
    setTemplateId(nextId);
    setValues(next ? initialValues(next) : {});
    setOverflowKey('');
    setErrors({ ...errors, template: '', params: '' });
  };

  const handleValueChange = (key, value) => {
    setValues({ ...values, [key]: value });
    setErrors({ ...errors, params: '' });
  };

  // Вставка, которая не влезает в поле: браузер молча отрежет лишнее, мы об этом скажем.
  const handleValuePaste = (key, room) => (event) => {
    const input = event.target;
    const pasted = (event.clipboardData && event.clipboardData.getData('text')) || '';
    const selected = (input.selectionEnd || 0) - (input.selectionStart || 0);
    const after = (values[key] || '').length - selected + pasted.length;
    setOverflowKey(after > room ? key : '');
  };

  // Печатают символ, когда поле уже заполнено до своего лимита.
  const handleValueKeyDown = (key, room) => (event) => {
    const printable = event.key && event.key.length === 1 && !event.ctrlKey && !event.metaKey;
    const input = event.target;
    const selected = (input.selectionEnd || 0) - (input.selectionStart || 0);
    if (printable && selected === 0 && (values[key] || '').length >= room) setOverflowKey(key);
  };

  // Приветствие с кнопками «Так / Ні» это запрос согласия: его получают те, кого ещё не
  // спрашивали. Обычная рассылка уходит только тем, кто согласие подтвердил.
  const welcome = Boolean(template && template.consentButtons);

  // Молчащие из списка: кому запрос уже уходил, а ответа нет.
  const silentContacts = useMemo(() => {
    if (!selectedList) return [];
    const idSet = new Set(selectedList.contactIds);
    return contacts.filter((c) => idSet.has(c.id) && c.consentStatus === 'pending');
  }, [selectedList, contacts]);
  const reaskable = useMemo(
    () => silentContacts.filter((c) => canReask(c)),
    [silentContacts]
  );

  const recipientContacts = useMemo(() => {
    if (!selectedList) return [];
    const idSet = new Set(selectedList.contactIds);
    return contacts.filter((c) => {
      if (!idSet.has(c.id)) return false;
      if (!welcome) return c.optIn;
      return (
        c.consentStatus === 'none' || (includeSilent && canReask(c))
      );
    });
  }, [selectedList, contacts, welcome, includeSilent]);

  const recipients = recipientContacts.length;
  const skippedContacts = stats.total - recipients;

  // Кто из списка не получит сообщение: их видно в панели состава списка.
  const skippedList = useMemo(() => {
    if (!selectedList) return [];
    const idSet = new Set(selectedList.contactIds);
    const going = new Set(recipientContacts.map((c) => c.id));
    return contacts.filter((c) => idSet.has(c.id) && !going.has(c.id));
  }, [selectedList, contacts, recipientContacts]);

  // Какая группа списка открыта в панели справа: 'going', 'skipped' или null — панель закрыта.
  const [audienceGroup, setAudienceGroup] = useState(null);
  const goingLabel = welcome ? 'Получат запрос согласия' : 'Получат сообщение';
  const skippedLabel = welcome ? 'Уже спрашивали или ответили' : 'Без согласия';

  // Текст для предпросмотра: имя берём у первого получателя списка.
  const previewParams = useMemo(
    () =>
      template
        ? paramsFor(template, values, recipientContacts[0] || null)
        : [],
    [template, values, recipientContacts]
  );
  const previewText = template ? renderTemplate(template, previewParams) : '';

  // Шаблон с картинкой: сервер рисует её по значениям из формы.
  const imageValues = {};
  previewParams.forEach((value, index) => {
    imageValues[String(index + 1)] = value;
  });
  const { url: imageUrl } = useRenderedImage(
    template && template.hasImage
      ? { template: { name: template.templateName, language: template.language }, values: imageValues }
      : null
  );

  // Лимит считаем по самому длинному имени в списке: у каждого получателя
  // имя своё, а сообщение не должно превысить лимит ни у кого.
  const longestContact = useMemo(
    () =>
      recipientContacts.reduce(
        (longest, contact) =>
          !longest || contact.name.length > longest.name.length
            ? contact
            : longest,
        null
      ),
    [recipientContacts]
  );
  const limitParams = useMemo(
    () => (template ? paramsFor(template, values, longestContact) : []),
    [template, values, longestContact]
  );
  const bodyLength = template ? renderTemplate(template, limitParams).length : 0;

  const validate = () => {
    const nextErrors = { ...emptyErrors };
    if (!name.trim()) {
      nextErrors.name = 'Введите название рассылки.';
    }
    if (!listId) {
      nextErrors.list = 'Выберите список получателей.';
    } else if (recipients === 0) {
      nextErrors.list = welcome
        ? 'В этом списке нет контактов, которых ещё не спрашивали о согласии.'
        : 'В этом списке нет контактов с согласием на рассылку.';
    }
    if (!template) {
      nextErrors.template = 'Выберите шаблон сообщения.';
    } else if (
      template.params.some(
        (param) => !param.auto && !(values[param.key] || '').trim()
      )
    ) {
      nextErrors.params = 'Заполните все поля шаблона.';
    } else if (bodyLength > BODY_MAX_LENGTH) {
      nextErrors.params = `Сообщение длиннее ${BODY_MAX_LENGTH} символов — сократите значения.`;
    }
    if (scheduled) {
      // Время идёт вперёд: пока форма была открыта, часть дат могла стать прошедшей.
      nextErrors.schedule = buildOccurrences(rule, kyivNow()).problem;
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).every((key) => !nextErrors[key]);
  };

  const handleOpenDialog = () => {
    if (validate()) {
      setDialogOpen(true);
    }
  };

  // Пробная отправка себе: нужен только готовый шаблон, список и название не нужны.
  const handleOpenTest = () => {
    if (!template) {
      setErrors({ ...errors, template: 'Выберите шаблон сообщения.' });
    } else if (
      template.params.some(
        (param) => !param.auto && !(values[param.key] || '').trim()
      )
    ) {
      setErrors({ ...errors, params: 'Заполните все поля шаблона.' });
    } else {
      setTestOpen(true);
    }
  };

  const resetForm = () => {
    clearDraft();
    setDraftRestored(false);
    setName('');
    setListId('');
    setTemplateId('');
    setValues({});
    setMode('now');
    setRule(defaultRule());
    setIncludeSilent(false);
    setErrors(emptyErrors);
  };

  const handleConfirmed = (created) => {
    // Отправили приветствие: статусы получателей стали «Ждём ответа».
    props.fetchConsents();
    // Часть дневного лимита потрачена.
    refreshLimits();
    const count = (created && created.count) || 1;
    props.showAlert({
      show: true,
      alertTitle: scheduled
        ? count > 1
          ? `Запланировано отправок: ${count}, первая ${formatFull(schedule.list[0])}`
          : `Рассылка запланирована на ${formatFull(schedule.list[0])}`
        : `Рассылка запущена — получателей: ${recipients}`,
      alertText: `«${name}» · WhatsApp${summary.cost !== null ? ` · ≈ ${summary.cost} грн` : ''}`,
      severity: 'success',
      style: {},
      variant: 'outlined',
    });
    // Окно закрыли, пока рассылка ещё идёт: сообщим тостом, когда она закончится.
    if (created && created.id && !['scheduled', 'done', 'failed', 'cancelled', 'paused'].includes(created.status)) {
      props.watchBroadcast(created.id, name);
    }
    setDialogOpen(false);
    resetForm();
  };

  const recipientList = useMemo(
    () =>
      template
        ? recipientContacts.map((contact) => ({
            phone: contact.phone,
            params: paramsFor(template, values, contact),
          }))
        : [],
    [template, values, recipientContacts]
  );

  // Стоимость: получатели x отправки x цена сообщения этой категории по данным Meta (в гривнах).
  // Если цены ещё нет (в этой категории ещё не было платных отправок), суммы нет.
  const estimate = (() => {
    const sends = scheduled ? Math.max(1, schedule.list.length) : 1;
    const info = template && pricing && pricing.perMessage ? pricing.perMessage[template.category] : null;
    if (!pricing) return { cost: null, note: 'Цена загружается у Meta…' };
    if (!info || info.priceUah == null) {
      return {
        cost: null,
        note: info
          ? 'Курс гривны недоступен, цена есть только в валюте аккаунта Meta.'
          : 'Цены этой категории у Meta ещё нет: она появится после первой платной отправки.',
      };
    }
    return {
      cost: (recipients * sends * info.priceUah).toFixed(2),
      note: `По цене Meta ${moneyLabel(info.priceUah)} грн за сообщение (на ${info.observedOn.split('-').reverse().join('.')}).`,
    };
  })();

  const summary = {
    name,
    listName: selectedList ? selectedList.name : '',
    recipients,
    recipientList,
    welcome,
    template,
    cost: estimate.cost,
    costNote: estimate.note,
    scheduled,
    // Один момент или серия: что показать в окне и что отправить серверу.
    sendAt:
      scheduled && schedule.list.length > 0
        ? schedule.list.length === 1
          ? formatFull(schedule.list[0])
          : `Отправок: ${schedule.list.length}, с ${formatFull(schedule.list[0])} по ${formatFull(
              schedule.list[schedule.list.length - 1]
            )}`
        : '',
    schedulePayload: scheduled ? payloadOf(rule, schedule.list) : {},
    scheduleCount: scheduled ? schedule.list.length : 1,
  };

  const counterColor =
    bodyLength > BODY_MAX_LENGTH ? 'error' : 'textSecondary';

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={7}>
        <Paper className={classes.card}>
          <div className={classes.cardHead}>
            <Typography className={classes.cardTitle}>Создание рассылки</Typography>
            {draftRestored && <span className={classes.draftNote}>Черновик восстановлен</span>}
            {(name || listId || templateId) && (
              <Button size="small" onClick={resetForm} className={classes.clearButton}>
                Очистить
              </Button>
            )}
          </div>

          <FieldLabel
            label="Название рассылки"
            hint={errors.name ? '' : 'Видно только менеджерам'}
            error={errors.name}
          >
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Акция на семена"
              value={name}
              error={Boolean(errors.name)}
              onChange={(event) => {
                setName(event.target.value);
                setErrors({ ...errors, name: '' });
              }}
            />
          </FieldLabel>

          <FieldLabel label="Канал">
            <ChannelSwitch />
          </FieldLabel>

          <FieldLabel
            label="Шаблон"
            error={errors.template || props.templatesError}
            hint={
              props.templatesLoading
                ? 'Загружаем шаблоны из Meta…'
                : 'Отправить можно только одобренные Meta шаблоны'
            }
          >
            <TextField
              select
              fullWidth
              variant="outlined"
              value={templateId}
              error={Boolean(errors.template)}
              SelectProps={{
                displayEmpty: true,
                // В закрытом поле картинка компактнее, чтобы оно не вытягивалось по высоте.
                renderValue: (value) => {
                  const chosen = sendableTemplates.find((item) => item.id === value);
                  if (!chosen) {
                    return <span style={{ color: colors.textDim }}>Выберите шаблон</span>;
                  }
                  return (
                    <span style={{ display: 'flex', alignItems: 'center' }}>
                      <TemplateThumb template={chosen} compact />
                      <span>{templateLabel(chosen)}</span>
                    </span>
                  );
                },
              }}
              onChange={handleTemplateChange}
            >
              <MenuItem value="" disabled>
                <span style={{ color: colors.textDim }}>Выберите шаблон</span>
              </MenuItem>
              {sendableTemplates.map((item) => (
                <MenuItem
                  key={item.id}
                  value={item.id}
                  disabled={item.status !== 'approved'}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    <TemplateThumb template={item} />
                    <span>{templateLabel(item)}</span>
                  </span>
                </MenuItem>
              ))}
            </TextField>
          </FieldLabel>
          {template && (
            <div className={classes.templateInfo}>
              <Chip
                className={
                  template.status === 'approved'
                    ? classes.chipOk
                    : classes.chipPending
                }
                label={statusInfo(template.status).label}
              />
              <Typography variant="caption" color="textSecondary">
                {template.description}
              </Typography>
            </div>
          )}

          <FieldLabel label="Список получателей" error={errors.list}>
            <TextField
              select
              fullWidth
              variant="outlined"
              value={listId}
              error={Boolean(errors.list)}
              SelectProps={{ displayEmpty: true }}
              onChange={(event) => {
                setListId(event.target.value);
                setErrors({ ...errors, list: '' });
              }}
            >
              <MenuItem value="" disabled>
                <span style={{ color: colors.textDim }}>Выберите список</span>
              </MenuItem>
              {lists.map((list) => (
                <MenuItem key={list.id} value={list.id}>
                  {list.name} —{' '}
                  {contactsLabel(listStats(list, contacts).total)}
                </MenuItem>
              ))}
            </TextField>
          </FieldLabel>
          {selectedList && (
            <div className={classes.audience}>
              <div className={classes.audienceChips}>
                {/* Плашки открывают панель «Список …» сразу на своей группе. */}
                <Chip
                  clickable
                  className={classes.chipOk}
                  icon={<CheckCircleIcon style={{ color: colors.okText }} />}
                  onClick={() => setAudienceGroup('going')}
                  label={`${goingLabel}: ${recipients}`}
                />
                {limits && limits.limit !== null && (
                  <Chip
                    className={
                      recipients > limits.remaining ? classes.chipWarn : classes.chipOk
                    }
                    icon={
                      recipients > limits.remaining ? (
                        <WarningIcon style={{ color: colors.warnText }} />
                      ) : (
                        <CheckCircleIcon style={{ color: colors.okText }} />
                      )
                    }
                    label={`Лимит номера на сегодня: осталось ${limits.remaining} из ${limits.limit}`}
                  />
                )}
                {skippedContacts > 0 && (
                  <Chip
                    clickable
                    className={classes.chipWarn}
                    icon={<WarningIcon style={{ color: colors.warnText }} />}
                    onClick={() => setAudienceGroup('skipped')}
                    label={`${skippedLabel}: ${skippedContacts}`}
                  />
                )}
              </div>
              <AudienceDrawer
                open={Boolean(audienceGroup)}
                group={audienceGroup || 'going'}
                onClose={() => setAudienceGroup(null)}
                listName={selectedList.name}
                going={recipientContacts}
                skipped={skippedList}
                goingLabel={goingLabel}
                skippedLabel={skippedLabel}
                onEditList={() => {
                  window.location.hash = `#/whatsapplists?edit=${encodeURIComponent(selectedList.id)}`;
                }}
                // «Нужно собрать согласие» в списках рассылки, только с контактами этого списка.
                onCollectConsent={
                  welcome
                    ? null
                    : () => {
                        window.location.hash = `#/whatsapplists?tab=consent&list=${encodeURIComponent(selectedList.id)}`;
                      }
                }
              />
              {welcome && silentContacts.length > 0 && (
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={includeSilent}
                      disabled={reaskable.length === 0}
                      onChange={(event) => setIncludeSilent(event.target.checked)}
                    />
                  }
                  label={
                    reaskable.length > 0
                      ? `Повторить запрос тем, кто не ответил (${reaskable.length} из ${silentContacts.length})`
                      : `Тем, кто не ответил (${silentContacts.length}), пока рано или уже поздно повторять запрос`
                  }
                />
              )}
              <Typography variant="caption" className={classes.hint}>
                {limits && limits.limit !== null && recipients > limits.remaining
                  ? `Получателей больше, чем осталось в дневном лимите номера (${limits.remaining}). Те, с кем вы уже писали сегодня, не считаются, но если новых окажется больше, сервер рассылку не отправит. `
                  : ''}
                {welcome
                  ? `Это запрос согласия: он уходит тем, кого ещё не спрашивали, и, если отметить галочку, тем, кто не ответил (не чаще раза в ${REASK_DAYS} дня и не больше ${MAX_ASKS} запросов на человека). Ответ клиента (кнопка «Так / Ні» или СТОП) запишется в карточку контакта сам.`
                  : 'Контакты без подтверждённого согласия исключаются автоматически — рассылка без opt-in запрещена законом. Состав списка правится на странице «Списки рассылки».'}
              </Typography>
            </div>
          )}

          {template && (
            <>
              {template.params.map((param, index) => {
                // Сколько знаков ещё помещается в это поле: всё сообщение не длиннее BODY_MAX_LENGTH.
                const room = Math.max(0, BODY_MAX_LENGTH - bodyLengthWithout(template, limitParams, index));
                const value = values[param.key] || '';
                // У каждого поля свой лимит: сколько осталось от всего сообщения после остальных полей.
                // Предупреждение только у поля, куда текст не поместился, и пока оно заполнено до предела.
                const full = overflowKey === param.key && value.length >= room;
                return param.auto ? (
                  <FieldLabel key={param.key} label={param.label}>
                    <div className={classes.autoValue}>
                      Подставится автоматически из карточки контакта
                    </div>
                  </FieldLabel>
                ) : (
                  <FieldLabel key={param.key} label={param.label}>
                    <TextField
                      fullWidth
                      variant="outlined"
                      multiline={Boolean(param.multiline)}
                      rows={param.multiline ? 4 : undefined}
                      placeholder={param.placeholder}
                      inputProps={{
                        style: { resize: 'vertical' },
                        maxLength: room,
                      }}
                      value={value}
                      error={Boolean(errors.params)}
                      helperText={
                        full
                          ? `В это поле помещается не больше ${room} символов (всё сообщение до ${BODY_MAX_LENGTH}). Лишнее не вставилось: сократите текст здесь или в других полях.`
                          : ''
                      }
                      FormHelperTextProps={{ style: { color: colors.warnText } }}
                      onPaste={handleValuePaste(param.key, room)}
                      onKeyDown={handleValueKeyDown(param.key, room)}
                      onChange={(event) =>
                        handleValueChange(param.key, event.target.value)
                      }
                    />
                  </FieldLabel>
                );
              })}
              {errors.params && (
                <Typography variant="caption" color="error">
                  {errors.params}
                </Typography>
              )}
              <Typography
                variant="caption"
                color={counterColor}
                className={classes.counter}
              >
                {bodyLength} / {BODY_MAX_LENGTH}
              </Typography>
              <Typography variant="caption" className={classes.hint}>
                Текст шаблона утверждён Meta и не меняется — заполняются только
                поля выше. Переносы строк в значениях заменяются пробелом.
              </Typography>
            </>
          )}
        </Paper>

        <Paper className={classes.card}>
          <Typography className={classes.cardTitle}>Отправка</Typography>
          <RadioGroup
            value={mode}
            onChange={(event) => setMode(event.target.value)}
          >
            <FormControlLabel
              value="now"
              control={<Radio color="primary" />}
              label="Отправить сейчас"
            />
            <FormControlLabel
              value="schedule"
              control={<Radio color="primary" />}
              label="Запланировать"
            />
          </RadioGroup>
          {scheduled && (
            <div style={{ marginTop: 16 }}>
              <ScheduleEditor
                rule={rule}
                result={schedule}
                error={errors.schedule}
                onChange={(next) => {
                  setRule(next);
                  setErrors({ ...errors, schedule: '' });
                }}
              />
            </div>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <div className={classes.sticky}>
          <WhatsappPreview
            text={previewText}
            imageUrl={imageUrl}
            footer={template ? template.footer : ''}
            time={scheduled && schedule.list[0] ? clockOf(schedule.list[0]) : currentTime()}
            // Две кнопки отправки под превью должны оставаться на экране.
            reserve={120}
          />
          <Button
            fullWidth
            variant="contained"
            disableElevation
            className={classes.sendButton}
            startIcon={scheduled ? <ScheduleIcon /> : <SendIcon />}
            onClick={handleOpenDialog}
          >
            {scheduled ? 'Запланировать рассылку' : 'Отправить рассылку'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            className={classes.testButton}
            onClick={handleOpenTest}
          >
            Отправить себе для проверки
          </Button>
        </div>
      </Grid>

      {template && (
        <TestSendDialog
          open={testOpen}
          template={template}
          params={previewParams}
          name={name.trim()}
          onClose={() => setTestOpen(false)}
        />
      )}

      <SendBroadcastDialog
        open={dialogOpen}
        summary={summary}
        onClose={() => setDialogOpen(false)}
        onConfirmed={handleConfirmed}
      />
    </Grid>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(CreateBroadcast);
